import { isNil } from 'lodash';
import Router from 'next/router';
import isEqual from 'react-fast-compare';

import { ASSET_MODAL_PARAMS_TO_OMIT } from '~/constants/assetModalParams';
import { Pathname } from '~/constants/pathname';
import { createBoardCanvasRoute, Routes } from '~/constants/routes';
import { QueryParamNames, type QueryType } from '~/constants/search';
import { emitPushedRouterHref } from '~/utils/subscribePushedRouterHref';
import { getCurrentQuery, isLocal, uuidRegExpString } from '~/utils/Url';

const boardIdRegExp = new RegExp(`/b/[^/?]*(${uuidRegExpString}).*`, 'i');
const libraryIdRegExp = new RegExp(`/l/[^/?]*(${uuidRegExpString}).*`, 'i');
const personIdRegExp = new RegExp(`/p/[^/?]*(${uuidRegExpString}).*`, 'i');
const clipIdRegExp = new RegExp(`/c/(${uuidRegExpString}).*`, 'i');
const ASSET_MODAL_CLIP_SEGMENT_REG_EXP = new RegExp(`/c/${uuidRegExpString}(?:[/?#]|$)`, 'i');
// Workspace editor is `/canvases/{uuid}`. Board editor is `/b/{boardId}/canvas/{uuid}`
// (singular `canvas`, distinct from `/c/{clipId}`). Trailing `[/?#]|$` keeps query,
// hash, and trailing slash from being treated as part of the id.
const PRIVATE_CANVAS_ID_REG_EXP = new RegExp(`^(?:/canvases|/b/[^/?]+/canvas)/(${uuidRegExpString})(?:[/?#]|$)`, 'i');
const PRIVATE_CANVAS_DRAFT_PATH_REG_EXP = /^\/b\/[^/]+\/canvas\/new$/;
const PRIVATE_CANVAS_BOARD_PARENT_REG_EXP = new RegExp(`^(/b/[^/]+)/canvas/(?:new|${uuidRegExpString})/?$`, 'i');
const shortIdRegExp = /\/a\/([0-9a-zA-Z]{9})/;
const savedSearchIdRegExp = new RegExp(`/saved-search/[^/?]*(${uuidRegExpString}).*`, 'i');
// Anchored, and the id must sit directly under `/showcases/` — the sibling `/showcases/preview/{id}`
// route shares this prefix, and only the leading `^` + immediate-id shape keeps `preview` from being
// read as a showcase id. (The pre-`/showcases` path had disjoint `/collection/` and `/preview/`
// segments, so it could afford a looser pattern.)
const showcaseIdRegExp = new RegExp(`^/showcases/(${uuidRegExpString})(?:[/?#]|$)`, 'i');

export const getShortIdFromPath = (pathname: string) => {
  const match = pathname.match(shortIdRegExp);

  return Array.isArray(match) ? match[1] : undefined;
};

export const getBoardIdFromPath = (pathname: string) => {
  const match = pathname.match(boardIdRegExp);

  return Array.isArray(match) ? match[1] : undefined;
};

export const getLibraryIdFromPath = (pathname: string) => {
  const match = pathname.match(libraryIdRegExp);

  return Array.isArray(match) ? match[1] : undefined;
};

export const getPersonIdFromPath = (pathname: string) => {
  const match = pathname.match(personIdRegExp);

  return Array.isArray(match) ? match[1] : undefined;
};

export const getSavedSearchIdFromPath = (pathname: string) => {
  const match = pathname.match(savedSearchIdRegExp);

  return Array.isArray(match) ? match[1] : undefined;
};

export const getShowcaseIdFromPath = (pathname: string) => {
  const match = pathname.match(showcaseIdRegExp);

  return Array.isArray(match) ? match[1] : undefined;
};

export const getClipIdFromPath = (pathname: string) => {
  const match = pathname.match(clipIdRegExp);

  return Array.isArray(match) ? match[1] : undefined;
};

export const getAssetModalParentHref = (path: string) => {
  const hashIndex = path.indexOf('#');
  const pathWithoutHash = hashIndex === -1 ? path : path.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : path.slice(hashIndex);

  const queryIndex = pathWithoutHash.indexOf('?');
  const pathname = queryIndex === -1 ? pathWithoutHash : pathWithoutHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : pathWithoutHash.slice(queryIndex + 1);

  const parentPathname = pathname.replace(ASSET_MODAL_CLIP_SEGMENT_REG_EXP, '') || Routes.media.all;
  const search = getQueryWithoutParams(query, ASSET_MODAL_PARAMS_TO_OMIT).toString();

  return `${parentPathname}${search ? `?${search}` : ''}${hash}`;
};

/** Private canvas editor page (`/canvases/{uuid}` or `/b/{boardId}/canvas/{uuid}`). Does not match `/canvases`, `/canvases/import/…`, `/canvases/new`, or `/b/…/canvases/{uuid}`. */
export const getPrivateCanvasIdFromPath = (pathname: string) => {
  const match = pathname.match(PRIVATE_CANVAS_ID_REG_EXP);

  return Array.isArray(match) ? match[1] : undefined;
};

/** Draft editor URL (`/canvases/new` or `/b/{boardId}/canvas/new`). Served by `[canvasId].tsx` so commit can replace to a uuid without remounting. */
export const isPrivateCanvasDraftPath = (path: string) => {
  const pathname = path.split(/[?#]/)[0];

  return pathname === Routes.canvasNew || PRIVATE_CANVAS_DRAFT_PATH_REG_EXP.test(pathname);
};

export const isPrivateCanvasPagePathname = (pathname: string) =>
  pathname === Routes.canvas || pathname === Routes.canvasNew || pathname === Pathname.BoardCanvas;

export type PrivateCanvasBoardHrefParams = {
  boardId: string;
  boardTitle?: string;
};

export const getPrivateCanvasPageHref = (canvasId: string, board?: PrivateCanvasBoardHrefParams) => {
  if (!board) {
    return {
      pathname: Routes.canvas,
      query: { canvasId },
    };
  }

  return createBoardCanvasRoute({
    canvasId,
    boardId: board.boardId,
    boardTitle: board.boardTitle,
  });
};

export const getPrivateCanvasDraftHref = (board?: PrivateCanvasBoardHrefParams) => {
  if (!board) {
    return Routes.canvasNew;
  }

  return getPrivateCanvasPageHref('new', board);
};

export const getBoardHrefFromPrivateCanvasPath = (path: string) => {
  const match = path.split(/[?#]/)[0].match(PRIVATE_CANVAS_BOARD_PARENT_REG_EXP);

  return Array.isArray(match) ? match[1] : undefined;
};

/** Board view Canvases tab. A missing `tab` query means Content. */
export const CANVAS_TAB_PARAM_VALUE = 'canvas';

export const getPrivateCanvasParentHref = (path: string) => {
  const boardHref = getBoardHrefFromPrivateCanvasPath(path);

  if (!boardHref) {
    return Routes.canvases;
  }

  // Back reconstructs the parent with push, so without this query the board remounts on Content.
  return formatPathWithQuery(boardHref, { [QueryParamNames.tab]: CANVAS_TAB_PARAM_VALUE });
};

type GetCanvasOpenHrefParams = {
  boardInfo?: PrivateCanvasBoardHrefParams;
  canvasId: string;
};

/** Cmd/middle-click destination for opening a canvas. `boardInfo` is the opening board, not canvas membership. */
export const getCanvasOpenHref = ({ boardInfo, canvasId }: GetCanvasOpenHrefParams) =>
  getPrivateCanvasPageHref(canvasId, boardInfo);

export const getSharePointErrorFromSearch = (urlParams?: string) => {
  const error = new URLSearchParams(urlParams).get('error');

  if (error) {
    const errorReason = JSON.parse(error).extra?.reason || '';

    return errorReason?.includes('No Sharepoint License') || false;
  }

  return false;
};

export const isImportPath = (pathname: string) => {
  return pathname.includes('/import/');
};

export const getPathWithoutQuery = (pathname: string) => pathname.split('?')[0];

export const getQueryWithoutParams = (query: string, paramsToOmit: string[]) => {
  const urlParams = new URLSearchParams(query);
  paramsToOmit.forEach((param) => {
    urlParams.delete(param);
  });

  return urlParams;
};

export const getPathWithOmittedParams = (asPath: string, paramsToOmit: string[]) => {
  const [pathname, query] = asPath.split('?');
  const paramsString = getQueryWithoutParams(query, paramsToOmit).toString();

  return paramsString ? `${pathname}?${paramsString}` : pathname;
};

type PathQueryValue = QueryType[keyof QueryType];

type PathQuery = Record<string, PathQueryValue>;

const isPathQueryPrimitive = (value: unknown): value is string | number | boolean | string[] =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || Array.isArray(value);

const copySearchQuery = (input: ReturnType<typeof getCurrentQuery>): PathQuery => {
  const query: PathQuery = {};

  for (const [key, value] of Object.entries(input)) {
    if (isNil(value) || !isPathQueryPrimitive(value)) {
      continue;
    }

    query[key] = value;
  }

  return query;
};

const appendQueryParam = (params: URLSearchParams, key: string, value: string | string[] | number | boolean) => {
  if (Array.isArray(value)) {
    for (const item of value) {
      params.append(key, String(item));
    }
    return;
  }

  params.append(key, String(value));
};

/**
 * Builds an exact href so path-changing navigations replace the query string instead of letting
 * Next merge leftover params (e.g. `am-tab=comments` surviving a clip id change).
 */
export const formatPathWithQuery = (pathname: string, query: PathQuery): string => {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (isNil(value)) {
      continue;
    }

    appendQueryParam(params, key, value);
  }

  const search = params.toString();

  return search ? `${pathname}?${search}` : pathname;
};

export const pushWithExistingQuery = ({
  path = Router.asPath,
  newQuery = {},
  method = 'push',
  shallow = true,
}: {
  path?: string;
  newQuery?: QueryType;
  method?: RouterMethod;
  shallow?: boolean;
}) => {
  const query = copySearchQuery(getCurrentQuery());
  const newQueryParams: PathQuery = {};
  for (const [paramName, value] of Object.entries(newQuery)) {
    if (isNil(value)) {
      delete query[paramName];
    } else {
      newQueryParams[paramName] = value;
    }
  }

  const queryToSet = { ...query, ...newQueryParams };
  const pathWithoutQuery = getPathWithoutQuery(path);
  const href = formatPathWithQuery(pathWithoutQuery, queryToSet);
  const hrefChanged =
    !isEqual(queryToSet, getCurrentQuery()) || pathWithoutQuery !== getPathWithoutQuery(Router.asPath);
  const hasExplicitQueryUpdate = Object.keys(newQuery).length > 0;

  if (hrefChanged) {
    const routerFunc = method === 'replace' ? Router.replace : Router.push;

    // Pass the same string as `url` and `as` so Next cannot reattach omitted query params
    // from the current route when only the `/c/<clipId>` segment changes.
    const navigation = routerFunc(href, href, { shallow });
    // Shallow query/clip-param updates often never emit `routeChangeComplete` with this href.
    // Listeners (asset-modal tab) apply it here, in the same tick as the click.
    emitPushedRouterHref(href);
    return navigation;
  }

  // Same href but an explicit `newQuery` (e.g. reopen comments while `am-tab` is already set).
  // Skip router push; listeners still need the href in the same tick as the click.
  if (hasExplicitQueryUpdate) {
    emitPushedRouterHref(href);
  }
};

export type RouterMethod = 'push' | 'replace';

export const setQueryParams = (newQuery: QueryType, method: RouterMethod = 'push', shallow = true) =>
  pushWithExistingQuery({ newQuery, method, shallow });

export const pushWithNewParams = (pathname: string, urlParams?: URLSearchParams) => {
  const queryString = urlParams?.toString();
  const urlToGo = queryString ? `${pathname}?${queryString}` : pathname;

  return Router.push(urlToGo, undefined, { shallow: true });
};

// plasmic does not work if it detects call for window.top
export const isCypressRun = () =>
  isLocal() && (typeof window !== 'undefined' ? !!window.Cypress || !!window.top?.Cypress : false);
